// Medications, the optional dose log, and vaccinations (SPEC.md §4.3 Medications).
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';
import type { DoseRow, MedicationRow, PlannedDose } from '../lib/meds';
import { plannedSessions, type ScheduleException, type TreatmentSchedule } from '../lib/schedule';
import { addDays, format, parse } from 'date-fns';

const MED_COLUMNS = `id, name, strength, form, dose, category, times, with_meals, day_rule, specific_days, given_at_center,
  start_date, end_date, prescribed_by, notes, active`;

export function useMedications() {
  const query = useDbQuery<MedicationRow>(`SELECT ${MED_COLUMNS} FROM medications WHERE deleted_at IS NULL ORDER BY active DESC, name COLLATE NOCASE`, [], ['medications']);
  return { medications: query.data ?? [], isLoading: query.isLoading };
}

/** Including deleted ones, so reminders can cancel their calendar events. */
export function useAllMedicationsForReminders() {
  const query = useDbQuery<MedicationRow & { deleted_at: string | null }>(`SELECT ${MED_COLUMNS}, deleted_at FROM medications`, [], ['medications']);
  return query.data ?? [];
}

/** Doses logged with a scheduled time in [from, to] ('yyyy-MM-dd'). */
export function useMedDoses(from: string, to: string) {
  const query = useDbQuery<DoseRow>(
    'SELECT id, medication_id, scheduled_for, status, logged_at FROM med_doses WHERE deleted_at IS NULL AND scheduled_for >= ? AND scheduled_for < ? ORDER BY scheduled_for',
    [from, `${to}T99`],
    ['med_doses']
  );
  return query.data ?? [];
}

export type MedicationInput = Omit<MedicationRow, 'id'>;

export function saveMedication(db: DbClient, id: string | undefined, values: MedicationInput) {
  return id ? db.update<MedicationRow>('medications', id, values) : db.insert<MedicationRow>('medications', values);
}

export function deleteMedication(db: DbClient, id: string) {
  return db.softDelete('medications', id);
}

/** Marks a dose taken or skipped. The id is fixed per medicine and time, so devices converge. */
export function logDose(db: DbClient, dose: PlannedDose, status: DoseRow['status']) {
  return db.upsert('med_doses', dose.doseId, { medication_id: dose.med.id, scheduled_for: dose.scheduledFor, status, logged_at: new Date().toISOString() });
}

export function clearDose(db: DbClient, doseId: string) {
  return db.softDelete('med_doses', doseId);
}

/**
 * Dates in [from, to] with dialysis, planned or logged, for "dialysis days" medicines. A logged
 * session counts even off-schedule; a missed one doesn't.
 */
export function dialysisDates(
  schedules: TreatmentSchedule[],
  exceptions: ScheduleException[],
  sessions: { session_date: string; status: string }[],
  from: string,
  to: string
) {
  const start = parse(from, 'yyyy-MM-dd', new Date());
  const end = parse(to, 'yyyy-MM-dd', new Date());
  const dates = new Set(plannedSessions(schedules, exceptions, start, end).map((session) => session.date));
  for (const session of sessions) {
    if (session.session_date < from || session.session_date > to) continue;
    if (session.status === 'missed') dates.delete(session.session_date);
    else dates.add(session.session_date);
  }
  return dates;
}

export const dateKey = (date: Date) => format(date, 'yyyy-MM-dd');
export const daysBefore = (date: string, days: number) => dateKey(addDays(parse(date, 'yyyy-MM-dd', new Date()), -days));

/* ------------------------------------------------------------------ Vaccinations */

export interface Vaccination {
  id: string;
  vaccine_name: string;
  dose_number: string | null;
  date_given: string;
  next_due_date: string | null;
  notes: string | null;
}

export function useVaccinations() {
  const query = useDbQuery<Vaccination>(
    'SELECT id, vaccine_name, dose_number, date_given, next_due_date, notes FROM vaccinations WHERE deleted_at IS NULL ORDER BY date_given DESC',
    [],
    ['vaccinations']
  );
  return { vaccinations: query.data ?? [], isLoading: query.isLoading };
}

export function saveVaccination(db: DbClient, id: string | undefined, values: Omit<Vaccination, 'id'>) {
  return id ? db.update('vaccinations', id, values) : db.insert('vaccinations', values);
}

export function deleteVaccination(db: DbClient, id: string) {
  return db.softDelete('vaccinations', id);
}
